import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import "./App.css";

import { fetchMe, isLoggedIn } from "./components/utils/auth";

import LoginPage from "./components/routes/LoginPage";
import ResetPasswordPage from "./components/routes/ResetPasswordPage";
import Home from "./components/routes/Home";
import ProjectRequestForm from "./components/clients/ProjectRequestForm";
import Settings from "./components/routes/Settings";

import Leads from "./components/routes/partners/Leads";
import Commissions from "./components/routes/partners/Commissions";
import Meetings from "./components/routes/Meetings";
import MeetingAvailabilityPage from "./components/routes/MeetingAvailabilityPage";
import NewMeetingPage from "./components/routes/NewMeetingPage";
import MeetingDetailPage from "./components/routes/MeetingDetailPage";
import Notifications from "./components/routes/Notifications";
import Profile from "./components/routes/Profile";
import Lead from "./components/routes/partners/Lead";
import PartnerClients from "./components/routes/partners/Clients";
import PartnerClient from "./components/routes/partners/Client";

import Onboarding from "./components/routes/Onboarding";
import OnboardingRole from "./components/partners/onboarding/OnboardingRole";
import OnboardingStripe from "./components/partners/onboarding/OnboardingStripe";
import OnboardingComplete from "./components/partners/onboarding/OnboardingComplete";

import StripeConnect from "./components/routes/partners/StripeConnect";

import ProtectedRoute from "./components/routes/PrivateRoutes";
import ProjectsPage from "./components/routes/client/ProjectsPage";
import ProjectDetailPage from "./components/routes/client/ProjectDetailPage";
import MessagesPage from "./components/routes/MessagesPage";
import MessageDetailPage from "./components/routes/MessageDetailPage";
import OfferDetailPage from "./components/offers/OfferDetailPage";
import OfferReviewPage from "./components/offers/OfferReviewPage";
import TemporaryClientOfferPage from "./components/offers/TemporaryClientOfferPage";
import InvoicePaymentPage from "./components/invoices/InvoicePaymentPage";
import ContactsPage from "./components/routes/ContactsPage";
import Files from "./components/routes/Files";
import EmployeesPage from "./components/routes/superuser/EmployeesPage";
import JobApplicationsPage from "./components/routes/superuser/JobApplicationsPage";
import JobApplicationDetailPage from "./components/routes/superuser/JobApplicationDetailPage";
import ClientsPage from "./components/routes/superuser/ClientsPage";
import ClientDetailPage from "./components/routes/superuser/ClientDetailPage";
import StaffProjectsPage from "./components/staff/StaffProjectsPage";
import StaffProjectWorkspace from "./components/staff/StaffProjectWorkspace";
import ProjectRequestsPage from "./components/staff/ProjectRequestsPage";
import ProjectRequestDetailPage from "./components/staff/ProjectRequestDetailPage";
import KnowledgeBasePage from "./components/staff/KnowledgeBasePage";
import TeamListPage from "./components/staff/team/TeamListPage";
import TeamDetailPage from "./components/staff/team/TeamDetailPage";
import ApprovalsPage from "./components/staff/approvals/ApprovalsPage";
import ApprovalDetailPage from "./components/staff/approvals/ApprovalDetailPage";
import WorkboardPage from "./components/staff/workboard/WorkboardPage";
import WorkboardTicketDetailPage from "./components/staff/workboard/WorkboardTicketDetailPage";
import SuperuserRoute from "./components/routes/superuser/SuperuserRoute";
import { features } from "./components/utils/features";


function App() {
  const initialRole = localStorage.getItem("portal_role") || (localStorage.getItem("is_superuser") === "true" ? "superuser" : localStorage.getItem("is_partner") === "true" ? "partner" : "client");

  const [portalRole, setPortalRole] = useState(initialRole);
  const [roleLoading, setRoleLoading] = useState(isLoggedIn());

  const applyUserRole = (data = {}) => {
    const nextRole = data.portal_role || (data.is_superuser ? "superuser" : data.is_partner ? "partner" : data.is_client ? "client" : "client");
    setPortalRole(nextRole);

    if (data.user_id) localStorage.setItem("user_id", data.user_id);
    if (typeof data.is_partner !== "undefined") {
      localStorage.setItem("is_partner", String(Boolean(data.is_partner || data.is_superuser)));
    }
    if (typeof data.is_client !== "undefined") {
      localStorage.setItem("is_client", String(Boolean(data.is_client)));
    }
    if (typeof data.is_internal !== "undefined") {
      localStorage.setItem("is_internal", String(Boolean(data.is_internal || data.is_superuser)));
    }
    if (typeof data.is_superuser !== "undefined") {
      localStorage.setItem("is_superuser", String(Boolean(data.is_superuser)));
    }
    if (typeof data.is_staff !== "undefined") {
      localStorage.setItem("is_staff", String(Boolean(data.is_staff)));
    }
    localStorage.setItem("portal_role", nextRole);
    localStorage.setItem("portal_roles", JSON.stringify(data.portal_roles || data.additional_roles || []));
    localStorage.setItem("permissions", JSON.stringify(data.permissions || []));
  };

  useEffect(() => {
    if (!isLoggedIn()) {
      setRoleLoading(false);
      return;
    }

    async function loadUser() {
      try {
        const data = await fetchMe();
        applyUserRole(data);
      } catch (err) {
        console.error(err);
      } finally {
        setRoleLoading(false);
      }
    }

    loadUser();
  }, []);

  const loggedIn = isLoggedIn();
  const effectiveRole = portalRole || localStorage.getItem("portal_role") || "client";
  const effectivePartner = ["partner", "superuser"].includes(effectiveRole);
  const meetingsEnabled = features.meetingsEnabled;
  const roleReady = !loggedIn || !roleLoading;

  return (
    <div className="App">
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Navigate to={loggedIn ? "/dashboard" : "/login"} replace />} />
        <Route
          path="/login"
          element={
            loggedIn ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage onAuthenticated={applyUserRole} />
            )
          }
        />
        <Route path="/reset-password/:uid/:token" element={<ResetPasswordPage />} />
        <Route path="/set-password/:uid/:token" element={<ResetPasswordPage />} />
        <Route path="/create-password/:uid/:token" element={<ResetPasswordPage />} />
        <Route path="/new-project" element={<ProjectRequestForm />} />
        <Route
          path="/new-project/:partnerId"
          element={<ProjectRequestForm />}
        />
        <Route path="/client-offers/:token" element={<TemporaryClientOfferPage />} />

        {/* Onboarding routes (no guards, no logic) */}
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/onboarding/role" element={<ProtectedRoute><OnboardingRole /></ProtectedRoute>} />
        <Route path="/onboarding/stripe" element={<ProtectedRoute><OnboardingStripe /></ProtectedRoute>} />
        <Route path="/onboarding/complete" element={<ProtectedRoute><OnboardingComplete /></ProtectedRoute>} />

        {/* Stripe routes */}
        <Route
          path="/stripe-connect/refresh/scd-portal"
          element={<ProtectedRoute><StripeConnect isSuccess={false} /></ProtectedRoute>}
        />

        <Route
          path="/stripe-connect/success/scd-portal"
          element={<ProtectedRoute><StripeConnect isSuccess={true} /></ProtectedRoute>}
        />

        {/* Protected routes (ONLY auth check) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              {roleReady ? <Home role={effectiveRole} partner={effectivePartner} /> : <div className="portal-loading">Loading dashboard...</div>}
            </ProtectedRoute>
          }
        />


        <Route
          path="/inbox"
          element={
            <ProtectedRoute>
              <MessagesPage partner={effectivePartner} />
            </ProtectedRoute>
          }
        />
        <Route path="/messages" element={<Navigate to="/inbox" replace />} />
        <Route
          path="/invoices/:invoiceId"
          element={
            <ProtectedRoute>
              <InvoicePaymentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contacts"
          element={
            <ProtectedRoute>
              <ContactsPage partner={effectivePartner} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/job-applications/:applicationId"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["hr"]} allowedPermissions={["manage_job_applications"]}>
                <JobApplicationDetailPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/job-applications"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["hr"]} allowedPermissions={["manage_job_applications"]}>
                <JobApplicationsPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["hr"]} allowedPermissions={["manage_employees"]}>
                <EmployeesPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route path="/partners" element={<Navigate to="/employees?group=sales" replace />} />
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              {effectiveRole === "partner" ? (
                <PartnerClients />
              ) : (
                <SuperuserRoute allowedRoles={["project_manager"]} allowedPermissions={["view_all_clients"]}>
                  <ClientsPage />
                </SuperuserRoute>
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/:clientId"
          element={
            <ProtectedRoute>
              {effectiveRole === "partner" ? (
                <SuperuserRoute allowedRoles={["partner"]} allowedPermissions={["view_own_clients", "view_all_clients"]}>
                  <PartnerClient />
                </SuperuserRoute>
              ) : (
                <SuperuserRoute allowedRoles={["project_manager"]} allowedPermissions={["view_all_clients"]}>
                  <ClientDetailPage />
                </SuperuserRoute>
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings role={effectiveRole} partner={effectivePartner} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/availability"
          element={
            <ProtectedRoute>
              {meetingsEnabled ? <MeetingAvailabilityPage partner={effectivePartner} /> : <Navigate to="/settings" replace />}
            </ProtectedRoute>
          }
        />

        <Route
          path="/leads"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["partner"]} allowedPermissions={["view_own_leads", "view_all_leads"]}><Leads /></SuperuserRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/leads/:leadId"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["partner"]} allowedPermissions={["view_own_leads", "view_all_leads"]}><Lead /></SuperuserRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/commissions"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["partner"]} allowedPermissions={["view_own_commissions", "view_all_commissions"]}><Commissions /></SuperuserRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/meetings"
          element={
            <ProtectedRoute>
              {meetingsEnabled ? <Meetings partner={effectivePartner} /> : <Navigate to="/dashboard" replace />}
            </ProtectedRoute>
          }
        />
        <Route path="/meetings/availability" element={<Navigate to={meetingsEnabled ? "/settings/availability" : "/settings"} replace />} />
        <Route
          path="/meetings/new"
          element={
            <ProtectedRoute>
              {meetingsEnabled ? <NewMeetingPage partner={effectivePartner} /> : <Navigate to="/dashboard" replace />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/meetings/:meetingId"
          element={
            <ProtectedRoute>
              {meetingsEnabled ? <MeetingDetailPage partner={effectivePartner} /> : <Navigate to="/dashboard" replace />}
            </ProtectedRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications partner={effectivePartner} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile partner={effectivePartner} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["partner", "hr", "developer", "designer", "marketing", "project_manager", "staff"]}>
                <KnowledgeBasePage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge/:identifier"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["partner", "hr", "developer", "designer", "marketing", "project_manager", "staff"]}>
                <KnowledgeBasePage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-requests"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["hr", "project_manager"]} allowedPermissions={["manage_project_status", "review_project_offers"]}>
                <ProjectRequestsPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-requests/:requestId"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["hr", "project_manager"]} allowedPermissions={["manage_project_status", "review_project_offers"]}>
                <ProjectRequestDetailPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/team"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["hr", "developer", "designer", "marketing", "project_manager", "staff"]}>
                <TeamListPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/team/:teamId"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["hr", "developer", "designer", "marketing", "project_manager", "staff"]}>
                <TeamDetailPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/approvals"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["hr", "project_manager"]}>
                <ApprovalsPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/approvals/:approvalId"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["hr", "project_manager"]}>
                <ApprovalDetailPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />        <Route
          path="/workboard"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["developer", "designer", "marketing", "project_manager", "staff"]}>
                <WorkboardPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workboard/team/:teamId"
          element={<Navigate to="/workboard" replace />}
        />
        <Route
          path="/workboard/tickets/:ticketId"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["developer", "designer", "marketing", "project_manager", "staff"]}>
                <WorkboardTicketDetailPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              {["superuser", "hr", "developer", "designer", "marketing", "project_manager", "staff"].includes(effectiveRole) ? <StaffProjectsPage /> : <ProjectsPage />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <ProtectedRoute>
              {["superuser", "hr", "developer", "designer", "marketing", "project_manager", "staff"].includes(effectiveRole) ? <StaffProjectWorkspace /> : <ProjectDetailPage />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages/:conversationId"
          element={
            <ProtectedRoute>
              <MessageDetailPage partner={effectivePartner} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers/review"
          element={
            <ProtectedRoute>
              <SuperuserRoute allowedRoles={["hr", "project_manager"]} allowedPermissions={["review_project_offers"]}>
                <OfferReviewPage />
              </SuperuserRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/offers/:offerId"
          element={
            <ProtectedRoute>
              <OfferDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/files"
          element={
            <ProtectedRoute>
              <Files />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to={loggedIn ? "/dashboard" : "/"} replace />} />
      </Routes>
    </div>
  );
}

export default App;







